import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Star, MapPin, Calendar, Image as ImageIcon, X } from "lucide-react";
import { Modal, ModalHeader, ModalContent } from "./ui/modal";
import { Badge } from "./ui/badge";

const PhotoModal = ({ isOpen, onClose, review }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!review) return null;
  
  // Ensure images exist and is an array
  let images = Array.isArray(review.images) ? review.images : [];
  if (images.length === 0) {
    console.log('No images found for review:', review);
    return null;
  }

  // Use backend proxy for external images
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  images = images.map(url => {
    // Use proxy for all external images to bypass CORS and authentication issues
    if (url.includes('storage.googleapis.com') || url.includes('drive.google.com')) {
      return `${backendUrl}/api/proxy-image?url=${encodeURIComponent(url)}`;
    }
    
    // Return original URL for other images
    return url;
  });

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-6xl">
      <ModalHeader>
        <div className="flex items-center justify-between pr-8">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{review.service}</h3>
            <div className="flex items-center space-x-2 mt-2">
              <Badge variant="outline" className="text-green-700 border-green-200">
                {review.postcode}
              </Badge>
              <div className="flex text-yellow-400">
                {[...Array(10)].map((_, i) => (
                  <Star key={i} className={`w-3 h-3 ${i < Math.floor(review.rating) ? 'fill-current' : ''}`} />
                ))}
              </div>
              <span className="font-semibold">{review.rating}/10</span>
            </div>
          </div>
        </div>
      </ModalHeader>
      
      <ModalContent className="space-y-6">
        {/* Image Gallery */}
        <div className="relative">
          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={images[currentImageIndex]}
              alt={`Professional work by ${review.name} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('❌ Image failed to load:', images[currentImageIndex]);
                
                // Hide the image and show error message
                e.target.style.display = 'none';
                
                // Add error message if not already present
                if (!e.target.parentNode.querySelector('.error-message')) {
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'error-message';
                  const isCheckatradeImage = images[currentImageIndex].includes('storage.googleapis.com');
                  const message = isCheckatradeImage 
                    ? 'This Checkatrade review photo link has expired. Contact us to see our latest work photos!'
                    : 'This image could not be loaded';
                  errorDiv.innerHTML = `<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(135deg, #1e293b 0%, #334155 100%); color: white; padding: 48px; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
                    <div style="font-size: 64px; margin-bottom: 20px;">📸</div>
                    <div style="font-size: 22px; font-weight: 600; margin-bottom: 12px; max-width: 500px; line-height: 1.4;">Photo Currently Unavailable</div>
                    <div style="font-size: 16px; opacity: 0.9; max-width: 450px; line-height: 1.6;">${message}</div>
                    ${isCheckatradeImage ? '<div style="margin-top: 20px; padding: 12px 24px; background: rgba(34, 197, 94, 0.2); border: 2px solid rgba(34, 197, 94, 0.4); border-radius: 8px; font-size: 14px;">✓ Verified Checkatrade Review</div>' : ''}
                  </div>`;
                  e.target.parentNode.style.position = 'relative';
                  e.target.parentNode.appendChild(errorDiv);
                }
              }}
              onLoad={() => {
                console.log('✅ Modal image loaded successfully:', images[currentImageIndex]);
              }}
            />
          </div>
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          
          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
        
        {/* Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentImageIndex ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </button>
            ))}
          </div>
        )}
        
        {/* Review Details */}
        <div className="space-y-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Customer Review</h4>
            <p className="text-gray-600 leading-relaxed text-lg">
              "{review.text}"
            </p>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{review.date}</span>
            </div>
            <div className="flex items-center space-x-1">
              <MapPin className="w-4 h-4" />
              <span>{review.postcode}</span>
            </div>
            <div className="flex items-center space-x-1">
              <ImageIcon className="w-4 h-4" />
              <span>{images.length} photo{images.length !== 1 ? 's' : ''}</span>
            </div>
            <span>•</span>
            <span>{review.name}</span>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default PhotoModal;